import { Component, ElementRef, EventEmitter, inject, Input, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Supplier } from 'src/app/models/supplier.model';
import { InventoryService } from 'src/app/services/inventory.service';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';
import { ImageService } from 'src/app/shared/services/image.service';

@Component({
  selector: 'app-modal-provider', 
  template: `
    <ng-template #modalProvider let-modal>
      <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">Proveedor</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
      </div>
      <div class="modal-body p-4">
        <form [formGroup]="supplierForm" (ngSubmit)="saveSupplier()">
          <div class="form-group mt-4">
            <label for="avatar" class="form-control-label d-block">Avatar</label>
            <div class="avatar-container mb-2">
              <img 
                [src]="avatarPreview || supplier?.avatar || 'assets/images/default-avatar.png'" 
                class="avatar-preview"
                alt="Supplier avatar">
            </div>
            <input 
              type="file" 
              class="form-control" 
              id="avatar"
              accept="image/*"
              (change)="onFileSelected($event)">
          </div>
          <div class="form-group mt-4">
            <label for="name" class="form-control-label">Nombre</label>
            <input 
              type="text" 
              class="form-control" 
              formControlName="name">
            <div *ngIf="sf['name'].touched && sf['name'].invalid" class="text-danger">
              <small *ngIf="sf['name'].errors?.['required']">Nombre is required</small>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="email" class="form-control-label">Correo electrónico</label>
            <input 
              type="email" 
              class="form-control" 
              formControlName="email">
            <div *ngIf="sf['email'].touched && sf['email'].invalid" class="text-danger">
              <small *ngIf="sf['email'].errors?.['required']">Correo electrónico is required</small>
              <small *ngIf="sf['email'].errors?.['email']">Please enter a valid email</small>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="phone" class="form-control-label">Teléfono</label>
            <input 
              type="tel" 
              class="form-control" 
              formControlName="phone">
          </div>

          <div class="form-group mt-4">
            <label for="address" class="form-control-label">Dirección</label>
            <textarea 
              class="form-control" 
              formControlName="address" 
              rows="3"></textarea>
          </div>

          <div class="form-group mt-4">
            <label for="uid" class="form-control-label">RFC</label>
            <input 
              type="text" 
              class="form-control" 
              formControlName="uid">
          </div>

          <div class="form-group mt-4 text-end">
            <button type="button" class="btn btn-link me-2" (click)="dismiss()">Cancel</button>
            <button type="submit" class="btn btn-zazz-dark" [disabled]="!supplierForm.valid">Save</button>
          </div>
        </form>
      </div>
    </ng-template>
  `,
  styles: [`
    .avatar-container {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid #ddd;
    }
    .avatar-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `]
})
export class ModalProviderComponent {
  @Input() supplier: Supplier | null = null;
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalProvider') modalProvider!: ElementRef;
  storageService = inject(StorageService);
  private modalRef: NgbModalRef | undefined;
  supplierForm: FormGroup = new FormGroup({});
  avatarPreview: string | null = null;
  avatarFile: File | null = null;

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private toastr: ToastrService,
    private imageService: ImageService
  ) {
    this.initForm();
  }

  initForm() {
    this.supplierForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      address: [''],
      uid: [''],
      avatar: [''],
      profile_id: ['']
    });
  }

  get sf() {
    return this.supplierForm.controls;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['supplier'] && this.supplier) {
      this.supplierForm.patchValue(this.supplier);
      this.avatarPreview = this.supplier.avatar || null;
    }
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        this.toastr.error('File size should not exceed 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        this.toastr.error('Only image files are allowed');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.avatarPreview = reader.result as string;
        // Store base64 string in form
        this.supplierForm.patchValue({
          avatar: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  }

  saveSupplier() {
    if (this.supplierForm.valid) {
      this.supplierForm.patchValue({
        profile_id: this.storageService.getItem(STORAGE_KEYS.PROFILE) ? this.storageService.getItem(STORAGE_KEYS.PROFILE).profile.id : null
      });
      if (this.supplier && this.supplier.id) {        
        // EDIT MODE
        this.inventoryService.updateSupplier(this.supplier.id, this.supplierForm.value).subscribe({
          next: (response: any) => {
            this.toastr.success('Supplier updated successfully');
            this.updateDismiss.emit(response);
            this.dismiss();
          },
          error: (error: any) => {
            this.toastr.error('Error updating supplier: ' + error.message);
          }
        });
      } else {
        // CREATE MODE
        this.inventoryService.createSupplier(this.supplierForm.value).subscribe({
          next: (response: any) => {
            this.toastr.success('Supplier created successfully');
            this.updateDismiss.emit(response);
            this.dismiss();
          },
          error: (error: any) => {
            this.toastr.error('Error creating supplier: ' + error.message);
          }
        });
      }
    } else {
      this.toastr.error('Please fill in all required fields');
    }
  }

  openModal(modalContent: TemplateRef<any>, supplier?: Supplier) {
    if (supplier) {
      this.supplier = supplier;
      this.supplierForm.patchValue(supplier);
      this.avatarPreview = supplier.avatar || null;
    }
    this.modalRef = this.modalService.open(modalContent, { size: 'lg' });
  }

  dismiss() {
    if (this.modalRef) {
      this.updateDismiss.emit();
      this.modalRef.dismiss();
    }
  }
}
